import { useState, useRef, useEffect, useMemo, KeyboardEvent, useCallback } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { getLanguages, getRecentLanguages, type Language } from '@/app/[locale]/actions/languages';
import { invalidateUserSettingsCache, getUserSettings, updateUserSettings } from '@/app/[locale]/actions/settings';
import { Users } from 'lucide-react';
import { useTranslations, useLocale } from 'next-intl';
import { useRtl } from '@/utils/hooks/useRTL';
import { cn } from '@/lib/utils';

export type SelectedLanguage = {
  id: string;
  name?: string;
  default_variety_id?: string;
  default_script_id?: string;
} | undefined;

interface LangDropdownProps {
  canAutoDetect?: boolean;
  settingKey: "in" | "out";
  onLanguageSelected: (lang: SelectedLanguage) => void;
  detectedLanguage?: {
    name: string;
  } | null,
  selectedLangId?: string;
  dropdownXOffset?: number;
  title?: string;
  className?: string;
}

export default function LangDropdown(props: LangDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState<string | null>(
    props.selectedLangId || (props.canAutoDetect ? 'detect' : null)
  );
  const [userSelected, setUserSelected] = useState<boolean>(false);
  const [listHeight, setListHeight] = useState('calc(100dvh - 10rem)'); // Default height
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [recentItems, setRecentItems] = useState<string[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const originalWindowHeight = useRef<number>(0);  // Store original window height
  const desktopSearchRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const settingKey = `languageDropdown-${props.settingKey}`;

  const locale = useLocale();
  const queryClient = useQueryClient();
  const { direction } = useRtl();

  const [
    { data: fetchedLanguages = [] },
    { data: userSettings },
    { data: recentLanguages = [] }
  ] = useQueries({
    queries: [
      {
        queryKey: ['languages', locale],
        queryFn: () => getLanguages(locale),
        staleTime: 60_000
      },
      {
        queryKey: ['settings', settingKey],
        queryFn: () => getUserSettings(settingKey),
        staleTime: 10000 // 10 secs
      },
      {
        queryKey: ['recentLanguages', props.settingKey],
        queryFn: () => getRecentLanguages(props.settingKey),
        staleTime: 10000 // 10 secs
      }
    ]
  });

  const t = useTranslations('langDropdown');

  const languages = props.canAutoDetect ? [
    { id: 'detect', name: t('detectLanguage') },
    ...fetchedLanguages
  ] : fetchedLanguages;

  // Add this helper function to get language data from ID
  const getLanguageById = (id: string) => {
    return fetchedLanguages.find(lang => lang.id === id);
  };

  const getLanguageNameById = (id: string) => {
    return getLanguageById(id)?.name;
  };

  useEffect(() => {
    if (userSettings && !userSelected) {
      const lastUsedLang = userSettings['lastUsedLanguage'];

      if (lastUsedLang) {
        setSelectedItem(lastUsedLang);
        const languageData = getLanguageById(lastUsedLang);
        props.onLanguageSelected?.({
          id: lastUsedLang,
          name: languageData?.name,
          default_variety_id: languageData?.default_variety_id,
          default_script_id: languageData?.default_script_id
        });
      }
    }
  }, [userSettings, userSelected]);

  useEffect(() => {
    if (props.selectedLangId !== undefined) {
      setSelectedItem(props.selectedLangId);
    }
  }, [props.selectedLangId]);

  useEffect(() => {
    if (recentLanguages.length > 0) {
      setRecentItems(recentLanguages);
    }
  }, [recentLanguages]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Track visual viewport changes (e.g., when keyboard appears/disappears)
  useEffect(() => {
    if (window.visualViewport) {
      originalWindowHeight.current = window.visualViewport.height;
    }

    const handleResize = () => {
      if (window.visualViewport) {
        const currentHeight = window.visualViewport.height;
        const isMobile = window.innerWidth < 768;

        if (!isMobile) {
          setListHeight('calc(100vh-265px)');
        } else if (currentHeight >= originalWindowHeight.current * 0.95) {
          setListHeight('calc(100dvh - 10rem)');
        } else {
          // Keyboard is visible (mobile only)
          const newHeight = Math.max(currentHeight * 0.7, 200);
          setListHeight(`${newHeight}px`);
        }
      }
    };

    // Initial call to set correct height
    handleResize();

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  // Auto-focus for desktop search
  useEffect(() => {
    if (isOpen && desktopSearchRef.current && window.innerWidth >= 768) {
      desktopSearchRef.current.focus();
    }
  }, [isOpen]);

  // Reset search when opening dropdown
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setSelectedIndex(-1);
    }
  }, [isOpen]);

  const filteredItems = useMemo(() =>
    languages.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    ),
    [languages, searchTerm]
  );

  const handleItemClick = async (item: string) => {
    setSelectedItem(item);
    setUserSelected(true);
    closeDropdown();

    const languageData = getLanguageById(item);
    props.onLanguageSelected?.({
      id: item,
      name: languageData?.name,
      default_variety_id: languageData?.default_variety_id,
      default_script_id: languageData?.default_script_id
    });

    await updateUserSettings(settingKey, { lastUsedLanguage: item });
    // Invalidate the settings cache to reflect the change immediately
    queryClient.invalidateQueries({ queryKey: ['settings', settingKey] });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const filteredLength = filteredItems.length;

    switch (e.key) {
      case 'Escape':
        closeDropdown();
        break;
      case 'ArrowDown':
      case 'ArrowRight':
      case 'Tab':
        e.preventDefault();
        setSelectedIndex(prev => {
          const next = prev + 1 >= filteredLength ? 0 : prev + 1;
          itemRefs.current[next]?.scrollIntoView({ block: 'nearest' });
          return next;
        });
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
      case 'ShiftTab':
        e.preventDefault();
        setSelectedIndex(prev => {
          const next = prev - 1 < 0 ? filteredLength - 1 : prev - 1;
          itemRefs.current[next]?.scrollIntoView({ block: 'nearest' });
          return next;
        });
        break;
      case 'Enter':
        if (selectedIndex >= 0 && selectedIndex < filteredLength) {
          handleItemClick(filteredItems[selectedIndex].id);
        }
        break;
    }
  };

  // Clear item refs when filtered items change
  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, filteredItems.length);
  }, [filteredItems]);

  const itemClassName = "p-2 md:px-3 md:py-1 rounded-lg cursor-pointer transition-colors";

  // Add this after the state declarations
  const closeDropdown = useCallback(() => {
    // React 18 automatically batches these updates
    setIsOpen(false);
    setSearchTerm('');
    setSelectedIndex(-1);
  }, []);

  return (
    <div className={cn("relative touch-none", props.className)}>
      {/* Dropdown Button */}
      <Button
        ref={buttonRef}
        className="w-full"
        variant="secondary"
        onClick={() => {
          if (isOpen) {
            closeDropdown();
          } else {
            setIsOpen(true);
          }
        }}
      >
        <div className="flex items-center justify-between w-full truncate">
          <span>
            {languages.find(lang => lang.id === selectedItem)?.name || t('selectLanguage')}
          </span>

          {props.detectedLanguage && selectedItem === 'detect' && (
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <span className="opacity-70">({props.detectedLanguage.name})</span>
            </div>
          )}
        </div>
      </Button>

      {/* Dropdown Popup */}
      {isOpen && (
        <div ref={dropdownRef}
          style={{ '--dropdown-offset': `${props.dropdownXOffset ?? 0}px` } as React.CSSProperties}
          className={`fixed md:absolute inset-0 md:inset-auto md:mt-2 w-full md:w-[calc(50vw-20px)] md:max-w-[700px] bg-popover border rounded-md shadow-md z-50 md:p-0 md:[margin-left:var(--dropdown-offset)] ${direction}`}
          role="dialog"
          aria-label={t('selectLanguage')}>

          {/* Mobile Header */}
          <div className={`sticky top-0 p-3 md:hidden ${direction}`}>
            <div className="flex justify-between items-center mb-4">
              <div className="text-lg font-semibold">{props.title ?? t('language')}</div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => closeDropdown()}
              >
                ✕
              </Button>
            </div>

            {/* Search Bar */}
            <Input
              type="text"
              placeholder={t('search')}
              className="w-full nozoom"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>

          {/* Desktop Search Bar */}
          <div className="hidden md:block p-3">
            <Input
              ref={desktopSearchRef}
              type="text"
              placeholder={t('search')}
              className="w-full focus-pocus"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>

          {/* Scrollable List */}
          <div
            className="overflow-y-auto px-2 pb-2 overscroll-contain md:max-h-[calc(100vh-265px)]"
            style={{ maxHeight: listHeight }}
            role="listbox"
            tabIndex={0}
          >

            {/* Recent Items */}
            <div className='grid grid-cols-2 lg:grid-cols-3 gap-2 pb-4'>
              {recentItems
                .map((langId) => (
                  {
                    id: langId,
                    name: getLanguageNameById(langId)
                  }))
                .filter(lang => lang.name)
                .map((lang, index) => (
                  <div
                    key={lang.id}
                    className={`${itemClassName} hover:bg-accent ${index > 3 ? 'hidden lg:block' : ''}`}
                    onClick={() => handleItemClick(lang.id)}
                    role="option"
                    aria-selected={selectedIndex === index}
                    tabIndex={-1}>
                    {lang.name}
                  </div>
                ))}

            </div>

            <div className="border-b border-base-300 mb-4" />

            {/* Items */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
              {filteredItems.map((item, index) => (
                <div
                  key={index}
                  ref={el => { itemRefs.current[index] = el }}
                  className={`${itemClassName} ${selectedIndex === index ? 'bg-red-400' : 'hover:bg-accent'}`}
                  onClick={() => handleItemClick(item.id)}
                  role="option"
                  aria-selected={selectedIndex === index}
                  tabIndex={-1}
                >
                  {item.name}
                </div>
              ))}
            </div>
          </div>

        </div>
      )}
    </div>
  );
}